import clsx from 'clsx'
import {PasswordStrengthLevel, getPasswordStrength} from 'common/helpers/passwordStrength'
import {FC} from 'react'
import {useIntl} from 'react-intl'

const LEVEL_MESSAGE_ID: Record<PasswordStrengthLevel, string> = {
  weak: 'GENERAL.PASSWORD_STRENGTH_WEAK',
  fair: 'GENERAL.PASSWORD_STRENGTH_FAIR',
  good: 'GENERAL.PASSWORD_STRENGTH_GOOD',
  strong: 'GENERAL.PASSWORD_STRENGTH_STRONG',
}

const LEVEL_BG: Record<PasswordStrengthLevel, string> = {
  weak: 'bg-danger',
  fair: 'bg-warning',
  good: 'bg-info',
  strong: 'bg-success',
}

const LEVEL_TEXT: Record<PasswordStrengthLevel, string> = {
  weak: 'text-danger',
  fair: 'text-warning',
  good: 'text-info',
  strong: 'text-success',
}

type Props = {
  password: string
  className?: string
}

const PasswordStrengthMeter: FC<Props> = ({password, className}) => {
  const intl = useIntl()
  const {percent, level} = getPasswordStrength(password)

  if (!password) {
    return null
  }

  // Show checklist only when user started typing.
  const showChecklist = Boolean(password)

  const hasMinLength = password.length >= 8
  const hasUppercase = /[A-Z]/.test(password)
  const hasNumber = /\d/.test(password)
  const hasSymbol = /[^A-Za-z0-9]/.test(password)
  const passwordRules = [
    {id: 'GENERAL.PASSWORD_RULE_MIN_LENGTH', ok: hasMinLength},
    {id: 'GENERAL.PASSWORD_RULE_UPPERCASE', ok: hasUppercase},
    {id: 'GENERAL.PASSWORD_RULE_NUMBER', ok: hasNumber},
    {id: 'GENERAL.PASSWORD_RULE_SPECIAL', ok: hasSymbol},
  ]

  return (
    <div className={clsx('mb-3', className)}>
      <div className='d-flex align-items-center justify-content-between mb-1'>
        <span className='text-muted fs-7'>
          {intl.formatMessage({id: 'GENERAL.PASSWORD_STRENGTH'})}
        </span>
        <span className={clsx('fs-7 fw-semibold', LEVEL_TEXT[level])}>
          {intl.formatMessage({id: LEVEL_MESSAGE_ID[level]})}
        </span>
      </div>
      <div className='progress h-5px bg-light'>
        <div
          className={clsx('progress-bar', LEVEL_BG[level])}
          style={{width: `${percent}%`}}
          role='progressbar'
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>

      {showChecklist && (
        <div className='mt-4'>
          <div className='text-muted fs-7 mb-2'>
            {intl.formatMessage({id: 'GENERAL.PASSWORD_MUST_INCLUDE'})}
          </div>
          <div className='d-flex flex-column gap-2'>
            {passwordRules.map((rule) => (
              <div key={rule.id} className='d-flex align-items-center'>
                <i
                  className={clsx(
                    rule.ok ? 'fas fa-check' : 'fas fa-times',
                    'me-2',
                    rule.ok ? 'text-success' : 'text-danger'
                  )}
                  style={{fontSize: '1.1rem'}}
                />
                <span className={clsx('fs-7', rule.ok ? 'text-success' : 'text-danger')}>
                  {intl.formatMessage({id: rule.id})}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default PasswordStrengthMeter
